'use client';

/* ============================================================================
 * RequestDemoV2 - Linear-light demo request page
 * ============================================================================
 * Single focused conversion page. Two columns: editorial pitch on the left,
 * 3-step form on the right. Below: "what happens next" timeline.
 *
 * Form contract preserved 1:1 with the legacy DemoRequestForm:
 *   POST /api/request-demo
 *   { propertyType, country, companyName, numberOfRooms, fullName, role,
 *     email, phoneNumber, message, turnstileToken }
 *
 * Cloudflare Turnstile is conditionally rendered (gated on
 * NEXT_PUBLIC_TURNSTILE_SITE_KEY just like legacy).
 * ========================================================================= */

import React, {useRef, useState} from 'react';
import {useLocale, useTranslations} from 'next-intl';
import {Turnstile} from '@marsidev/react-turnstile';
import {Link} from '@/i18n/navigation';
import {apiPost} from '@/utils/api';
import {Reveal, RevealStagger, RevealLine} from '@/components/v2/Reveal';
import {getCountryOptions} from '@/components/v2/forms/countries';
import {
  TextField,
  TextArea,
  SelectField,
  NumberStepper,
  RadioCardGroup,
  StepIndicator,
  FormButton
} from '@/components/v2/forms/FormPrimitives';

const NS = 'Pages.RequestDemo';
const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

const PROPERTY_TYPE_KEYS = ['hotel', 'resort', 'hostel', 'rental', 'partnership', 'other'];

const ROLE_KEYS = [
  'Owner / Founder',
  'General Manager',
  'Operations Manager',
  'Sales & Marketing',
  'IT Department',
  'Revenue Management',
  'Consultant',
  'Other'
];

function Container({children, className = '', narrow = false}) {
  return (
    <div className={`${narrow ? 'container-narrow' : 'container-page'} ${className}`}>
      {children}
    </div>
  );
}

function SectionAnchor({id, label, total = 3}) {
  return (
    <Reveal className="mb-10 flex items-center gap-3 text-foreground-subtle">
      <span className="font-mono text-[11px] tracking-[0.16em] uppercase">
        {String(id).padStart(2, '0')} / {String(total).padStart(2, '0')}
      </span>
      <RevealLine className="block h-px flex-1 bg-border" delay={0.1} />
      <span className="font-mono text-[11px] tracking-[0.16em] uppercase">
        {label}
      </span>
    </Reveal>
  );
}

function SectionHeroAndForm() {
  const t = useTranslations(NS);
  const bullets = t.raw('hero.bullets');
  return (
    <section className="bg-hero-mesh relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28">
      <Container>
        <SectionAnchor id={1} label={t('anchors.requestDemo')} />

        <div className="grid grid-cols-1 gap-14 md:grid-cols-12 md:gap-12">
          <div className="md:col-span-5 md:pr-8">
            <Reveal eager>
              <p className="fig-label">{t('hero.eyebrow')}</p>
            </Reveal>
            <Reveal delay={0.06} eager>
              <h1 className="headline-hero mt-5 max-w-[16ch] text-foreground">
                {t('hero.headline')}
              </h1>
            </Reveal>
            <Reveal delay={0.12} eager>
              <p className="lead mt-6 max-w-[40ch]">{t('hero.lead')}</p>
            </Reveal>
            <Reveal delay={0.18} eager>
              <ul className="mt-10 space-y-4 text-[14px] leading-relaxed text-foreground-muted">
                {bullets.map((b) => (
                  <ChecklistItem key={b}>{b}</ChecklistItem>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.24} eager>
              <p className="mt-10 text-[12.5px] leading-relaxed text-foreground-subtle">
                {t('hero.existingPrefix')}{' '}
                <a href="https://www.prostay.com/support/" className="text-primary underline-offset-4 hover:underline">
                  {t('hero.existingLinkText')}
                </a>
                .
              </p>
            </Reveal>
          </div>

          <div className="md:col-span-7">
            <Reveal delay={0.18} eager>
              <DemoRequestCard />
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
}

function ChecklistItem({children}) {
  return (
    <li className="flex items-start gap-3">
      <svg className="mt-1 size-3.5 flex-shrink-0 text-primary" viewBox="0 0 14 14" fill="none" aria-hidden>
        <path
          d="M11.5 4L5.5 10L2.5 7"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span>{children}</span>
    </li>
  );
}

function DemoRequestCard() {
  const t = useTranslations(NS);
  const locale = useLocale();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const localizedRoles = ROLE_KEYS.map((k) => t(`roles.${k}`));
  const stepLabels = t.raw('form.stepLabels');

  const [propertyType, setPropertyType] = useState('');
  const [country, setCountry] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [totalRooms, setTotalRooms] = useState(20);
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState(localizedRoles[1]);
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);

  const turnstileRef = useRef(null);

  const isStep1Valid = Boolean(propertyType && country && companyName.trim());
  const isStep2Valid = Boolean(fullName.trim() && role);
  const isStep3Valid = Boolean(
    email.trim() &&
      phoneNumber.trim() &&
      (TURNSTILE_SITE_KEY ? turnstileToken : true)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isStep3Valid) return;
    try {
      setSubmitting(true);
      setError(null);
      await apiPost('/api/request-demo', {
        propertyType,
        country,
        companyName,
        numberOfRooms: totalRooms,
        fullName,
        role,
        email,
        phoneNumber,
        message,
        turnstileToken: turnstileToken ?? '',
        locale
      });
      setSubmitting(false);
      setTurnstileToken(null);
      turnstileRef.current?.reset();
      setStep(4);
    } catch (err) {
      console.error('Demo request failed', err);
      setSubmitting(false);
      turnstileRef.current?.reset();
      setError(t('form.error'));
    }
  };

  if (step === 4) {
    return <ConfirmationCard email={email} />;
  }

  return (
    <div className="surface-elevated p-7 md:p-8">
      {submitting ? (
        <SubmittingState />
      ) : (
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="overflow-x-auto pb-1">
            <StepIndicator steps={stepLabels} current={step} />
          </div>

          {step === 1 && (
            <Step1
              propertyType={propertyType}
              setPropertyType={setPropertyType}
              country={country}
              setCountry={setCountry}
              companyName={companyName}
              setCompanyName={setCompanyName}
              totalRooms={totalRooms}
              setTotalRooms={setTotalRooms}
            />
          )}

          {step === 2 && (
            <Step2
              fullName={fullName}
              setFullName={setFullName}
              role={role}
              setRole={setRole}
              localizedRoles={localizedRoles}
            />
          )}

          {step === 3 && (
            <Step3
              email={email}
              setEmail={setEmail}
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              message={message}
              setMessage={setMessage}
              turnstileRef={turnstileRef}
              setTurnstileToken={setTurnstileToken}
            />
          )}

          {error && (
            <div className="rounded-lg border border-danger-500 bg-danger-50 px-4 py-3 text-[13px] text-danger-700">
              {error}
            </div>
          )}

          <StepNav
            step={step}
            onBack={() => setStep((s) => Math.max(1, s - 1))}
            onNext={() => setStep((s) => Math.min(3, s + 1))}
            onSubmit={handleSubmit}
            canContinue={
              step === 1 ? isStep1Valid : step === 2 ? isStep2Valid : isStep3Valid
            }
          />
        </form>
      )}
    </div>
  );
}

function StepNav({step, onBack, onNext, onSubmit, canContinue}) {
  const t = useTranslations(NS);
  return (
    <div className="flex items-center justify-between gap-4 border-t border-border pt-6">
      {step > 1 ? (
        <FormButton variant="secondary" onClick={onBack} arrow="left">
          {t('form.backCta')}
        </FormButton>
      ) : (
        <span />
      )}

      {step < 3 ? (
        <FormButton onClick={onNext} disabled={!canContinue}>
          {t('form.continueCta')}
        </FormButton>
      ) : (
        <FormButton type="submit" onClick={onSubmit} disabled={!canContinue}>
          {t('form.submitCta')}
        </FormButton>
      )}
    </div>
  );
}

function Step1({
  propertyType,
  setPropertyType,
  country,
  setCountry,
  companyName,
  setCompanyName,
  totalRooms,
  setTotalRooms
}) {
  const t = useTranslations(NS);
  const locale = useLocale();
  const countryOptions = React.useMemo(() => getCountryOptions(locale), [locale]);
  const propertyOptions = PROPERTY_TYPE_KEYS.map((key) => ({
    value: key,
    label: t(`propertyTypes.${key}`),
    icon: <PropertyIcon name={key} />
  }));
  return (
    <div className="space-y-7">
      <div>
        <p className="fig-label">{t('form.step1.stepLabel')}</p>
        <h2 className="mt-2 text-2xl font-medium text-foreground tracking-tight">
          {t('form.step1.headline')}
        </h2>
      </div>

      <RadioCardGroup
        label={t('form.step1.propertyTypeLabel')}
        value={propertyType}
        onChange={setPropertyType}
        options={propertyOptions}
      />

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <SelectField
          id="country"
          label={t('form.step1.countryLabel')}
          value={country}
          onChange={setCountry}
          options={countryOptions}
          placeholder={t('form.step1.countryPlaceholder')}
          required
        />
        <TextField
          id="companyName"
          label={t('form.step1.companyLabel')}
          value={companyName}
          onChange={setCompanyName}
          placeholder={t('form.step1.companyPlaceholder')}
          required
        />
      </div>

      <NumberStepper
        id="totalRooms"
        label={t('form.step1.totalRoomsLabel')}
        value={totalRooms}
        onChange={setTotalRooms}
        min={1}
        max={9999}
      />
    </div>
  );
}

function Step2({fullName, setFullName, role, setRole, localizedRoles}) {
  const t = useTranslations(NS);
  return (
    <div className="space-y-7">
      <div>
        <p className="fig-label">{t('form.step2.stepLabel')}</p>
        <h2 className="mt-2 text-2xl font-medium text-foreground tracking-tight">
          {t('form.step2.headline')}
        </h2>
      </div>

      <TextField
        id="fullName"
        label={t('form.step2.fullNameLabel')}
        value={fullName}
        onChange={setFullName}
        placeholder={t('form.step2.fullNamePlaceholder')}
        autoComplete="name"
        required
      />

      <SelectField
        id="role"
        label={t('form.step2.roleLabel')}
        value={role}
        onChange={setRole}
        options={localizedRoles}
        required
      />
    </div>
  );
}

function Step3({
  email,
  setEmail,
  phoneNumber,
  setPhoneNumber,
  message,
  setMessage,
  turnstileRef,
  setTurnstileToken
}) {
  const t = useTranslations(NS);
  return (
    <div className="space-y-7">
      <div>
        <p className="fig-label">{t('form.step3.stepLabel')}</p>
        <h2 className="mt-2 text-2xl font-medium text-foreground tracking-tight">
          {t('form.step3.headline')}
        </h2>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <TextField
          id="email"
          label={t('form.step3.emailLabel')}
          type="email"
          value={email}
          onChange={setEmail}
          placeholder={t('form.step3.emailPlaceholder')}
          autoComplete="email"
          required
        />
        <TextField
          id="phoneNumber"
          label={t('form.step3.phoneLabel')}
          type="tel"
          value={phoneNumber}
          onChange={setPhoneNumber}
          placeholder={t('form.step3.phonePlaceholder')}
          autoComplete="tel"
          required
        />
      </div>

      <TextArea
        id="message"
        label={t('form.step3.messageLabel')}
        value={message}
        onChange={setMessage}
        placeholder={t('form.step3.messagePlaceholder')}
        rows={5}
      />

      {TURNSTILE_SITE_KEY && (
        <div>
          <Turnstile
            ref={turnstileRef}
            siteKey={TURNSTILE_SITE_KEY}
            onSuccess={setTurnstileToken}
            onError={() => setTurnstileToken(null)}
            onExpire={() => setTurnstileToken(null)}
            options={{theme: 'light'}}
          />
        </div>
      )}

      <p className="text-[12.5px] leading-relaxed text-foreground-subtle">
        {t('form.step3.privacyPrefix')}{' '}
        <a href="https://www.prostay.com/privacy-policy/" className="text-foreground underline-offset-4 hover:underline">
          {t('form.step3.privacyLinkText')}
        </a>
        {t('form.step3.privacySuffix')}
      </p>
    </div>
  );
}

function SubmittingState() {
  const t = useTranslations(NS);
  return (
    <div className="flex min-h-[420px] flex-col items-center justify-center gap-4">
      <div className="size-10 animate-pulse-soft rounded-full border-2 border-border-strong border-t-primary" />
      <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-foreground-subtle">
        {t('form.submittingLabel')}
      </p>
    </div>
  );
}

function ConfirmationCard({email}) {
  const t = useTranslations(NS);
  return (
    <div className="surface-elevated relative overflow-hidden p-8 md:p-10">
      <div className="glow-brand-soft" />
      <div className="relative space-y-6">
        <div className="inline-flex size-12 items-center justify-center rounded-full border border-primary bg-brand-50 text-primary">
          <svg viewBox="0 0 18 18" className="size-5" fill="none" aria-hidden>
            <path
              d="M3 9.5L7 13.5L15 5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div>
          <p className="fig-label">{t('confirmation.sentLabel')}</p>
          <h2 className="mt-3 text-3xl font-medium text-foreground tracking-tight">
            {t('confirmation.headline')}
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-foreground-muted max-w-[42ch]">
            {t('confirmation.body', {email})}
          </p>
        </div>
        <div className="flex flex-wrap gap-5 pt-2">
          <Link href="/" className="cta-pill">
            {t('confirmation.homeCta')} <span aria-hidden>&rarr;</span>
          </Link>
          <Link href="/features/" className="cta-text-brand">
            {t('confirmation.browseCta')} <span aria-hidden>&rarr;</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

function SectionWhatsNext() {
  const t = useTranslations(NS);
  const items = t.raw('whatsNext.items');
  return (
    <section className="section-rhythm-sm">
      <Container>
        <SectionAnchor id={2} label={t('anchors.whatsNext')} />
        <div className="mb-12 grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-7">
            <Reveal>
              <h2 className="headline-section text-foreground max-w-[20ch]">
                {t('whatsNext.headlineLine1')} <span className="text-foreground-muted">{t('whatsNext.headlineLine2')}</span>
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.06}>
              <p className="lead">{t('whatsNext.lead')}</p>
            </Reveal>
          </div>
        </div>

        <RevealStagger
          as="ul"
          mode="clone"
          className="grid grid-cols-1 divide-y divide-border border-y border-border md:grid-cols-3 md:divide-y-0 md:divide-x"
          gap={0.06}
        >
          {items.map((step, i) => (
            <li key={step.title} className="p-7 md:p-8">
              <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-foreground-subtle">
                {String(i + 1).padStart(2, '0')}
              </p>
              <h3 className="mt-3 text-xl font-medium text-foreground tracking-tight">
                {step.title}
              </h3>
              <p className="mt-3 text-[14px] leading-relaxed text-foreground-muted">
                {step.body}
              </p>
            </li>
          ))}
        </RevealStagger>
      </Container>
    </section>
  );
}

function SectionTrust() {
  const t = useTranslations(NS);
  const stats = t.raw('trust.stats');
  return (
    <section className="section-rhythm-sm pb-24">
      <Container>
        <SectionAnchor id={3} label={t('anchors.trust')} />
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-12">
          <div className="md:col-span-7">
            <Reveal as="h2" className="headline-manifesto text-foreground max-w-[22ch]">
              {t('trust.headlineLine1')} <span className="text-primary">{t('trust.headlineHighlight')}</span>
            </Reveal>
            <Reveal delay={0.08}>
              <p className="lead mt-6 max-w-[44ch]">{t('trust.lead')}</p>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <RevealStagger
              as="dl"
              mode="clone"
              className="grid grid-cols-2 gap-x-6 gap-y-6 border-t border-border pt-6"
              gap={0.06}
            >
              {stats.map((stat) => (
                <Stat key={stat.label} label={stat.label} value={stat.value} />
              ))}
            </RevealStagger>
          </div>
        </div>
      </Container>
    </section>
  );
}

function Stat({label, value}) {
  return (
    <div>
      <dt className="font-mono text-[11px] tracking-[0.16em] uppercase text-foreground-subtle">
        {label}
      </dt>
      <dd className="mt-2 text-2xl font-medium text-foreground tracking-tight">
        {value}
      </dd>
    </div>
  );
}

function PropertyIcon({name}) {
  const common = 'size-5';
  switch (name) {
    case 'hotel':
      return (
        <svg className={common} viewBox="0 0 20 20" fill="none" aria-hidden>
          <path
            d="M3 18V6a1 1 0 011-1h12a1 1 0 011 1v12M3 18h14M7 9h2M7 12h2M11 9h2M11 12h2M9 18v-3h2v3"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'resort':
      return (
        <svg className={common} viewBox="0 0 20 20" fill="none" aria-hidden>
          <path
            d="M3 16h14M5 16V9m10 7V9M3 9l7-5 7 5M9 16v-3h2v3"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M2 4c2 0 2 2 4 2s2-2 4-2 2 2 4 2 2-2 4-2"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'hostel':
      return (
        <svg className={common} viewBox="0 0 20 20" fill="none" aria-hidden>
          <path
            d="M3 5h14v10H3zM3 9h14M3 13h14M7 5v10M13 5v10"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'rental':
      return (
        <svg className={common} viewBox="0 0 20 20" fill="none" aria-hidden>
          <path
            d="M3 17V9l7-5 7 5v8M8 17v-5h4v5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'partnership':
      return (
        <svg className={common} viewBox="0 0 20 20" fill="none" aria-hidden>
          <path
            d="M7 11l3 3 3-3 3 3-3 3-3-3-3 3-3-3zM10 7L7 4M10 7l3-3"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'other':
    default:
      return (
        <svg className={common} viewBox="0 0 20 20" fill="none" aria-hidden>
          <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.4" />
          <path
            d="M8 8a2 2 0 014 0c0 1.5-2 1.5-2 3M10 14h.01"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        </svg>
      );
  }
}

export default function RequestDemoV2() {
  return (
    <>
      <SectionHeroAndForm />
      <SectionWhatsNext />
      <SectionTrust />
    </>
  );
}
